import {
    useReviewsPageDataContext,
    useReviewsPageQueryContext,
    useReviewsPageSelectionContext,
    useReviewsPageTransportContext,
} from '@/context/reviews-page.context';
import { useAuth } from '@/context/auth.context';
import { useProductsTableAnalysis } from '@/components/products-table/use-products-table-analysis';
import { useProductsGroupEditor } from '@/components/products-table/use-products-group-editor';
import { useProductsTableSelection } from '@/components/products-table/use-products-table-selection';
import {
    ProductsTableData,
    useProductsTableViewModel,
} from '@/components/products-table/use-products-table-view-model';
import { useSelectionProductsAction } from '@/components/action-buttons/use-selection-products-action';
import { extractApiErrorMessage } from '@/components/group-editor/extract-api-error-message';
import { createProductsTableColumns } from '@/components/products-table/products-table-columns';
import {
    buildProductsTableControllerBindings,
    type ProductsTableControllerBindings,
} from '@/components/products-table/products-table-controller-bindings.builder';
import { buildGroupTableControllerCore } from '@/components/group-tables/build-group-table-controller-core';
import type { ProductPhotoEntity } from '@/entities/product.entity';
import { useCallback, useEffect, useState } from 'react';

export type UseProductsTableControllerArgs = {
    productsPage: number;
    productsOnPage: number;
    setProductsPage: (page: number) => void;
    setProductsPageCount: (count: number) => void;
    setProductsTotalRows: (count: number) => void;
};

export type ProductsTableControllerValue = ProductsTableControllerBindings;

export const useProductsTableControllerOrchestration = ({
    productsPage,
    productsOnPage,
    setProductsPage,
    setProductsPageCount,
    setProductsTotalRows,
}: UseProductsTableControllerArgs): ProductsTableControllerValue => {
    const { products, brandGroups } = useReviewsPageDataContext();
    const { selectionSessionId, pickedProducts, setPickedProducts } = useReviewsPageSelectionContext();
    const { maxSelectedItems } = useReviewsPageQueryContext();
    const { getProducts } = useReviewsPageTransportContext();
    const { accessToken, clientId } = useAuth();
    const {
        nameFilter,
        setNameFilter,
        productFilter,
        setProductFilter,
        brandFilter,
        setBrandFilter,
        brandSearch,
        setBrandSearch,
        sort,
        preparedBrands,
        filteredAndSortedProducts,
        pagedProducts,
        onSortChanged,
    } = useProductsTableViewModel({
        clientId,
        products,
        brandGroups,
        productsPage,
        productsOnPage,
        setProductsPage,
        setProductsPageCount,
        setProductsTotalRows,
    });
    const { onHeaderCheckboxChange, onCheckboxChange, isHeaderChecked, checkboxData } = useProductsTableSelection({
        accessToken,
        selectionSessionId,
        pickedProducts,
        setPickedProducts,
        maxSelectedItems,
        visibleProducts: filteredAndSortedProducts,
        pagedProducts,
    });
    const { onAllPickedProducts } = useSelectionProductsAction({
        accessToken,
        selectionSessionId,
        hasPickedProducts: pickedProducts.length > 0,
        products: filteredAndSortedProducts,
        maxSelectedItems,
        setPickedProducts,
    });
    const { onProductClick, onProductAiOpen, ...analysisBindings } = useProductsTableAnalysis<ProductsTableData>({
        accessToken,
    });
    const [openedProductPhotos, setOpenedProductPhotos] = useState<{
        photos: ProductPhotoEntity[];
        index: number;
    } | null>(null);
    const [isProductPhotoLoading, setIsProductPhotoLoading] = useState(false);

    const onProductPhotoPreviewOpen = useCallback((photos: ProductPhotoEntity[], index: number) => {
        if (!photos.length) {
            return;
        }

        setIsProductPhotoLoading(true);
        setOpenedProductPhotos({ photos, index });
    }, []);

    const onProductPhotoPreviewClose = useCallback(() => {
        setIsProductPhotoLoading(false);
        setOpenedProductPhotos(null);
    }, []);

    const onProductPhotoPreviewPrevious = useCallback(() => {
        setOpenedProductPhotos((prevState) => {
            if (!prevState || prevState.photos.length < 2) {
                return prevState;
            }

            setIsProductPhotoLoading(true);
            const nextIndex = prevState.index === 0 ? prevState.photos.length - 1 : prevState.index - 1;
            return { ...prevState, index: nextIndex };
        });
    }, []);

    const onProductPhotoPreviewNext = useCallback(() => {
        setOpenedProductPhotos((prevState) => {
            if (!prevState || prevState.photos.length < 2) {
                return prevState;
            }

            setIsProductPhotoLoading(true);
            const nextIndex = prevState.index === prevState.photos.length - 1 ? 0 : prevState.index + 1;
            return { ...prevState, index: nextIndex };
        });
    }, []);

    useEffect(() => {
        if (!openedProductPhotos) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onProductPhotoPreviewClose();
                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                onProductPhotoPreviewPrevious();
                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                onProductPhotoPreviewNext();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onProductPhotoPreviewClose, onProductPhotoPreviewNext, onProductPhotoPreviewPrevious, openedProductPhotos]);

    const { onGroupEditorOpen, ...groupEditorBindings } = useProductsGroupEditor<ProductsTableData>({
        accessToken,
        getProducts,
        extractApiErrorMessage,
    });
    const { columns, headerSecondaryRow, groupEditorColumns } = createProductsTableColumns({
        pickedProducts,
        isHeaderChecked,
        onHeaderCheckboxChange,
        nameFilter,
        setNameFilter,
        productFilter,
        setProductFilter,
        brandFilter,
        setBrandFilter,
        brandSearch,
        setBrandSearch,
        preparedBrands,
        onProductAiOpen,
        onGroupEditorOpen,
        onProductPhotoPreviewOpen,
    });

    return buildGroupTableControllerCore({
        table: {
            columns,
            data: pagedProducts,
            rowKey: 'id',
            tableId: 'products-groups-table',
            tableScope: 'products',
            onSortChanged,
            currentSort: sort,
            pickedData: pickedProducts,
            onAllPicked: onAllPickedProducts,
            onHeaderCheckboxChange,
            onCheckboxChange,
            isHeaderChecked,
            checkboxData,
            headerSecondaryRow,
            onRowClick: onProductClick,
        },
        analysis: analysisBindings,
        groupEditor: {
            ...groupEditorBindings,
            groupEditorColumns,
            openedProductPhotos,
            isProductPhotoLoading,
            setIsProductPhotoLoading,
            onProductPhotoPreviewClose,
            onProductPhotoPreviewPrevious,
            onProductPhotoPreviewNext,
        },
        buildControllerBindings: buildProductsTableControllerBindings,
    });
};
